import { forwardRef } from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cn } from "../../../lib/cn";

/**
 * Phase 2 — Radix Toast styled with our tokens. Replaces the ad-hoc
 * `.toast-wrap` / `.toast` in common-ui.css and the unsaved/success
 * banners sprinkled through pages. Provider mounts the root Viewport;
 * Toast component is the individual notification.
 *
 * Usage (global): wrap the app tree in <ToastProvider><ToastViewport />
 * {app}</ToastProvider>. Fire notifications by rendering a <Toast>.
 * For richer imperative API, wire sonner in Phase 5; this is the
 * minimum viable shadcn-style primitive.
 */
export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = forwardRef(function ToastViewport({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Viewport
      ref={ref}
      className={cn(
        "fixed bottom-6 right-6 z-[9999] flex max-w-[380px] flex-col gap-2 outline-none",
        className,
      )}
      {...props}
    />
  );
});

export const Toast = forwardRef(function Toast({ className, tone = "default", ...props }, ref) {
  const toneClasses = {
    default: "bg-rc-text-primary text-rc-blue-900",
    info:    "bg-rc-accent text-white",
    success: "bg-[#10c981] text-rc-blue-900",
    warn:    "bg-[#f59e0b] text-rc-blue-900",
    crit:    "bg-rc-red text-white",
  };
  return (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(
        "flex items-start gap-3 min-w-[220px] max-w-[360px] rounded-[10px] px-[18px] py-[13px]",
        "shadow-rc-lg animate-fadeSlide",
        "data-[state=closed]:opacity-0 data-[swipe=end]:opacity-0",
        toneClasses[tone] || toneClasses.default,
        className,
      )}
      {...props}
    />
  );
});

export const ToastTitle = forwardRef(function ToastTitle({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Title
      ref={ref}
      className={cn("text-[13px] font-bold mb-0.5", className)}
      {...props}
    />
  );
});

export const ToastDescription = forwardRef(function ToastDescription({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Description
      ref={ref}
      className={cn("text-xs opacity-75", className)}
      {...props}
    />
  );
});

export const ToastClose = ToastPrimitive.Close;
export const ToastAction = ToastPrimitive.Action;
